import React, { useCallback, useRef, useState } from "react";
import { RefreshControl, StyleSheet, View } from "react-native";
import Animated from "react-native-reanimated";
import * as Sentry from "@sentry/react-native";
import { useRouter } from "expo-router";
import { FlatListWithHeaders } from "@codeherence/react-native-header";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import ProfileContent from "./ProfileContent";
import ProfileTabs, { ProfileScreenTab } from "./ProfileHeader/ProfileTabs";
import ProfileAvatar from "./ProfileHeader/ProfileAvatar";

import ProfileHeader from "@/screens/Profile/ProfileHeader";
import { type AppUserSession } from "@/schemas/auth/session";
import { type UserProfile } from "@/schemas/user/profile";
import { useMessageContext } from "@/context/messaging/StreamContext";
import { useChatClient } from "@/context/messaging/useChatClient";
import { Spacings } from "@/styling/spacings";

const PROFILE_HEADER_DEFAULT_HEIGHT = 340;
const PROFILE_AVATAR_TOP_MARGIN = 4;

type CollapseProfileProps = {
  session: AppUserSession;
  profile: UserProfile;
  onRefresh: () => Promise<void>;
};

function ScrollableProfile({
  session,
  profile,
  onRefresh,
}: CollapseProfileProps): React.JSX.Element {
  const { bottom } = useSafeAreaInsets();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState<ProfileScreenTab>(
    ProfileScreenTab.PUBLIC,
  );

  const router = useRouter();
  const { chatClient } = useChatClient();
  const { setChannel } = useMessageContext();
  const [isCreatingChannel, setIsCreatingChannel] = useState(false);
  const [largeHeaderHeight, setLargeHeaderHeight] = useState(
    PROFILE_HEADER_DEFAULT_HEIGHT,
  );

  // for scrolling to top when user clicks on header avatar
  const flatListRef = useRef<Animated.FlatList>(null);
  const scrollToTop = useCallback(() => {
    flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  const createAndRedirectToMessageChannel = async (): Promise<void> => {
    if (isCreatingChannel) {
      return;
    }

    setIsCreatingChannel(true);
    const members = [session.identity.username, profile.username].sort();
    const channelId = `${members[0]}-${members[1]}`;

    try {
      if (chatClient !== null) {
        const channel = chatClient.channel("messaging", channelId, {
          members,
        });
        await channel.create();
        // Possible that the user "deleted" the chat.
        await channel.addMembers([session.identity.username, profile.username]);
        if (setChannel !== null) {
          setChannel(channel);
          router.push("/(main)/messaging/channel");
        } else {
          throw new Error(
            "Something went wrong while creating the channel. Please try again.",
          );
        }
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsCreatingChannel(false);
    }
  };

  const handleSetSelectedTab = (tab: ProfileScreenTab): void => {
    // This is a hack solution to hide white screen when the user clicks on message.
    if (tab === ProfileScreenTab.MESSAGE) {
      const previousTab = selectedTab;
      createAndRedirectToMessageChannel();
      setSelectedTab(previousTab);
    } else {
      setSelectedTab(tab);
    }
  };

  const renderContent = (): React.JSX.Element => (
    <View style={styles.scrollView}>
      <ProfileContent
        session={session}
        profile={profile}
        selectedTab={selectedTab}
      />
    </View>
  );

  const LargeProfileHeaderComponent = (): React.JSX.Element => {
    return (
      <View
        style={styles.largeProfileHeaderContainer}
        onLayout={(e) => {
          setLargeHeaderHeight(
            e.nativeEvent.layout.height + PROFILE_AVATAR_TOP_MARGIN,
          );
        }}
      >
        <ProfileAvatar session={session} profile={profile} />
        <ProfileTabs
          selectedTab={selectedTab}
          setSelectedTab={handleSetSelectedTab}
        />
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <FlatListWithHeaders
        ref={flatListRef}
        HeaderComponent={(props) => (
          <ProfileHeader
            {...props}
            profile={profile}
            session={session}
            onPressHeader={scrollToTop}
            largeHeaderHeight={largeHeaderHeight}
          />
        )}
        LargeHeaderComponent={LargeProfileHeaderComponent}
        data={[{ key: "content" }]}
        renderItem={() => renderContent()}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        headerFadeInThreshold={0.4} // fading content appears at 40% scroll
        disableAutoFixScroll
        disableLargeHeaderFadeAnim
        contentContainerStyle={{ paddingBottom: bottom + Spacings.s4 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            progressViewOffset={largeHeaderHeight}
          />
        }
      ></FlatListWithHeaders>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  largeProfileHeaderContainer: {
    flex: 1,
    marginTop: PROFILE_AVATAR_TOP_MARGIN,
    zIndex: 2,
  },
  scrollView: {
    flex: 1,
    marginTop: Spacings.s1,
  },
});

export default ScrollableProfile;
